import { useCallback, useEffect, useRef, useState } from 'react'
import { TextInput } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconCheck, IconChevronLeft, IconChevronRight, IconEyeOff, IconMinus, IconPlus, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { V2SelectField, V2TextField, inputClassNames, isStyleTileField } from './V2Fields'
import { getAtPath } from '../../utils/configPaths'
import { sliderElementLimitReached } from '../../utils/sliderValidation'
import { deadlineDate, deadlineIso } from '../../utils/countdownDeadline'
import { templateThumbUrl } from '../../utils/uiGroups'
import { fieldLabel, isWideField } from '../../utils/uiLayoutV2'
import { HelpTip } from '../v2/HelpTip'
import { Segmented } from '../v2/Segmented'
import css from '../../styles/EditorV2.module.css'
import classes from './V2Form.module.css'

const LIMIT = 2
const CARD_STEP = 160 + 12

/**
 * Figma bullets row: "•" + input, "–" to remove a row, "+ Add another bullet" (up to the
 * field's maximum). Empty rows live only here; the ad receives the filled ones.
 */
function BulletList({ field, value, onChange, disabled, error }) {
  const { t } = useTranslation()
  const stored = Array.isArray(value) ? value : []
  const [rows, setRows] = useState(() => (stored.length ? stored : ['']))
  const max = field.validation?.maxItems || 4
  const placeholders = field.ui?.placeholders || []

  useEffect(() => {
    const filled = rows.filter((row) => row.trim())
    if (JSON.stringify(filled) !== JSON.stringify(stored)) setRows(stored.length ? stored : [''])
  }, [JSON.stringify(stored)]) // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (next) => {
    setRows(next)
    onChange(next.filter((row) => row.trim()))
  }

  return (
    <div className={classes.bullets}>
      {rows.map((row, index) => (
        <div key={index} className={classes.bulletRow}>
          <span className={classes.bulletMark} aria-hidden="true">•</span>
          <TextInput
            style={{ flex: 1 }}
            aria-label={t('editorV2.form.extras.bullet', { number: index + 1 })}
            placeholder={placeholders[index] || placeholders[placeholders.length - 1] || ''}
            value={row}
            maxLength={80}
            disabled={disabled}
            onChange={(event) => commit(rows.map((entry, position) => (position === index ? event.currentTarget.value : entry)))}
            classNames={inputClassNames}
          />
          {rows.length > 1 && (
            <button
              type="button"
              className={classes.iconButton}
              aria-label={t('editorV2.form.extras.removeBullet', { number: index + 1 })}
              disabled={disabled}
              onClick={() => commit(rows.filter((_, position) => position !== index))}
            >
              <IconMinus size={16} stroke={1.8} />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className={classes.addBullet}
        disabled={disabled || rows.length >= max}
        onClick={() => setRows([...rows, ''])}
      >
        <IconPlus size={14} stroke={1.8} />
        {t('editorV2.form.extras.addBullet')}
      </button>
      {error && <p className={classes.error}>{error}</p>}
    </div>
  )
}

function DeadlineField({ field, value, onChange, disabled, error }) {
  const { t } = useTranslation()
  return (
    <DatePickerInput
      label={fieldLabel(field)}
      placeholder={field.ui?.placeholder || t('editorV2.form.extras.datePlaceholder')}
      valueFormat="DD/MM/YYYY"
      value={deadlineDate(value)}
      onChange={(day) => onChange(deadlineIso(day, value))}
      rightSection={<IconCalendar size={16} stroke={1.6} />}
      rightSectionPointerEvents="none"
      clearable={false}
      minDate={deadlineDate(new Date().toISOString())}
      popoverProps={{ withinPortal: true }}
      disabled={disabled}
      error={error}
      classNames={inputClassNames}
    />
  )
}

function summaryText(element, item, fieldByPath) {
  return (element.fields || []).map((relative) => {
    const field = fieldByPath.get(relative)
    const value = getAtPath(item, relative)
    if (!field || value == null || value === '') return ''
    if (Array.isArray(value)) return value.filter((entry) => String(entry).trim()).join(', ')
    if (field.type === 'datetime') return (deadlineDate(value) || '').split('-').reverse().join('/')
    if (field.type === 'select') return (field.options || []).find((option) => option.value === value)?.label || String(value)
    return String(value).trim()
  }).filter(Boolean).join(' · ')
}

function ElementPicker({ cards, onToggle }) {
  const { t } = useTranslation()
  const track = useRef(null)
  const [overflow, setOverflow] = useState({ left: false, right: false })

  const measure = useCallback(() => {
    const node = track.current
    if (!node) return
    const max = node.scrollWidth - node.clientWidth
    setOverflow({ left: node.scrollLeft > 4, right: max > 4 && node.scrollLeft < max - 4 })
  }, [])

  useEffect(() => {
    measure()
    const node = track.current
    if (!node) return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure, cards.length])

  const page = (direction) => {
    const node = track.current
    if (!node) return
    const step = Math.max(1, Math.floor((node.clientWidth + 12) / CARD_STEP)) * CARD_STEP
    node.scrollBy({ left: direction * step, behavior: 'smooth' })
  }

  return (
    <>
      {(overflow.left || overflow.right) && (
        <div className={classes.pickPager}>
          <span className={classes.pager}>
            <button type="button" className={classes.pagerButton} aria-label={t('editorV2.form.extras.previous')} disabled={!overflow.left} onClick={() => page(-1)}>
              <IconChevronLeft size={14} stroke={1.8} />
            </button>
            <button type="button" className={classes.pagerButton} aria-label={t('editorV2.form.extras.more')} disabled={!overflow.right} onClick={() => page(1)}>
              <IconChevronRight size={14} stroke={1.8} />
            </button>
          </span>
        </div>
      )}
      <div ref={track} className={`${classes.track} ${classes.pickTrack}`} onScroll={measure} role="group" aria-label={t('editorV2.form.extras.group')}>
        {cards.map(({ element, thumb, enabled, blocked }) => (
          <button
            key={element.id}
            type="button"
            className={`${classes.thumbCard} ${classes.pickCard}`}
            data-selected={enabled ? 'true' : 'false'}
            data-locked={blocked ? 'true' : undefined}
            aria-pressed={enabled}
            aria-disabled={blocked || undefined}
            onClick={() => { if (!blocked) onToggle(element, enabled) }}
          >
            <span className={classes.thumbFrame}>
              {thumb ? (
                <img className={classes.thumbImage} src={thumb} alt="" loading="lazy" draggable="false" />
              ) : (
                <span className={classes.thumbFallback} aria-hidden="true" />
              )}
              {element.description && <span className={classes.pickInfo}>{element.description}</span>}
              {enabled ? (
                <span className={classes.pickCheck} aria-hidden="true"><IconCheck size={10} stroke={3} /></span>
              ) : (
                <span className={classes.pickHidden} title={t('editorV2.form.extras.notInSlide')}><IconEyeOff size={10} stroke={2.4} /></span>
              )}
            </span>
            <span className={classes.thumbCaption}>{element.title}</span>
          </button>
        ))}
      </div>
    </>
  )
}

/**
 * Extra Elements (per slide): a card slider picks the elements; each added one gets a section
 * that opens to its content fields or, through the Content | Style switch, to its ad-wide style
 * fields; one section is open at a time. Kicker, subline, bullets, countdown and info line share
 * the two-per-slide limit; the bubble does not count.
 */
export function ExtraElements({ sectionUi, basePath, item, fields, shared, errors, openElement, onOpen, modes, onMode }) {
  const { t } = useTranslation()
  const { configuration, updateValue, templateManifest, elementStyleFields = [], renderStyleField } = shared
  const [picking, setPicking] = useState(false)
  const elements = sectionUi?.elements || []
  if (!elements.length) return null
  const fieldByPath = new Map(fields.map((field) => [field.path, field]))
  const ids = elements.map((element) => element.id)
  // The info line's style sits under the Subline tab, so the path decides the owner before the tab.
  const ownerOf = (field) => ids.find((id) => field.path.split('.').includes(id)) || field.ui?.tab
  const title = sectionUi.elementsTitle || t('editorV2.form.extras.title')

  const cards = elements.map((element) => {
    const enabledPath = `${basePath}.${element.enabledPath}`
    const enabled = Boolean(getAtPath(item, element.enabledPath))
    const thumbParts = String(element.thumb || '').split('/')
    return {
      element,
      enabledPath,
      enabled,
      blocked: element.limited !== false && !enabled && sliderElementLimitReached(configuration, enabledPath),
      thumb: templateThumbUrl(templateManifest?.releaseKey, thumbParts.slice(0, -1).join('/'), thumbParts[thumbParts.length - 1]),
    }
  })
  const added = cards.filter((card) => card.enabled)
  const counted = added.filter((card) => card.element.limited !== false).length
  const free = elements.find((element) => element.limited === false)
  const pickerErrors = cards.filter((card) => !card.enabled && errors[card.enabledPath]).map((card) => errors[card.enabledPath])

  const toggleElement = (element, enabled) => {
    updateValue(`${basePath}.${element.enabledPath}`, !enabled)
    if (!enabled) onOpen(element.id)
    else if (openElement === element.id) onOpen(null)
  }

  return (
    <>
      <div className={classes.pickHead}>
        <span className={classes.heading}>{title}</span>
        <span className={classes.pickCount}>
          {t('editorV2.form.extras.count', { count: counted, limit: LIMIT })}{free ? ` · ${t('editorV2.form.extras.notCounted', { title: free.title })}` : ''}
        </span>
      </div>
      {pickerErrors.map((message) => <p key={message} className={classes.error}>{message}</p>)}
      <div className={classes.elements}>
        {added.map(({ element, enabledPath, thumb }) => {
          const styleFields = renderStyleField ? elementStyleFields.filter((field) => ownerOf(field) === element.id) : []
          const open = openElement === element.id
          const mode = styleFields.length && modes[element.id] === 'style' ? 'style' : 'content'
          const contentPaths = (element.fields || []).map((relative) => `${basePath}.${relative}`)
          const contentFields = (element.fields || []).map((relative) => ({ relative, field: fieldByPath.get(relative) })).filter((entry) => entry.field)
          const summary = !open ? summaryText(element, item, fieldByPath) : ''
          const hiddenError = !open && [...contentPaths, ...styleFields.map((field) => field.path)].map((path) => errors[path]).find(Boolean)

          return (
            <section key={element.id} className={classes.element} aria-label={element.title}>
              <div className={classes.elementHead} data-clickable="true" onClick={() => onOpen(open ? null : element.id)}>
                <div className={classes.elementMain}>
                  <div className={classes.elementTopRow}>
                    <button
                      type="button"
                      className={classes.elementToggle}
                      aria-expanded={open}
                      onClick={(event) => { event.stopPropagation(); onOpen(open ? null : element.id) }}
                    >
                      <IconChevronRight size={16} stroke={1.8} className={classes.elementChevron} data-open={open ? 'true' : 'false'} aria-hidden="true" />
                      <span className={classes.label}>{element.title}</span>
                    </button>
                    {element.description && <HelpTip label={element.description} />}
                    {styleFields.length > 0 && (
                      <div className={classes.elementMode} onClick={(event) => { event.stopPropagation(); onOpen(element.id) }}>
                        <Segmented
                          ariaLabel={t('editorV2.form.extras.contentOrStyle', { title: element.title })}
                          value={mode}
                          onChange={(next) => onMode(element.id, next)}
                          options={[{ value: 'content', label: t('editorV2.form.extras.content') }, { value: 'style', label: t('editorV2.form.extras.style') }]}
                          itemClassName={classes.elementModeItem}
                        />
                      </div>
                    )}
                    {summary && <span className={classes.elementSummary} title={summary}>{summary}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  className={classes.elementRemove}
                  aria-pressed="true"
                  onClick={(event) => { event.stopPropagation(); toggleElement(element, true) }}
                >
                  <IconMinus size={12} stroke={1.8} />
                  {t('editorV2.form.common.remove')}
                </button>
              </div>
              {errors[enabledPath] && <p className={classes.error}>{errors[enabledPath]}</p>}
              {hiddenError && <p className={classes.error}>{hiddenError}</p>}
              {open && (
                <div className={classes.elementBody}>
                  <span className={classes.elementThumb} aria-hidden="true">
                    {thumb ? <img src={thumb} alt="" /> : null}
                  </span>
                  <div className={classes.elementFields}>
                    <span className={classes.elementSectionTitle}>
                      {t(mode === 'style' ? 'editorV2.form.extras.titleStyle' : 'editorV2.form.extras.titleContent', { title: element.title })}
                      {mode === 'style' && <HelpTip label={t('editorV2.form.extras.appliesToAll')} />}
                    </span>
                    <div className={classes.elementGrid}>
                      {mode === 'style' ? styleFields.map((field) => (
                        <div key={field.path} className={(isWideField(field) && field.ui?.width !== 'half') || isStyleTileField(field) ? classes.full : classes.cell}>
                          {renderStyleField(field)}
                        </div>
                      )) : contentFields.map(({ field, relative }) => {
                        const path = `${basePath}.${relative}`
                        const common = {
                          field,
                          value: getAtPath(item, relative),
                          onChange: (next) => updateValue(path, next),
                          error: errors[path],
                        }
                        const list = field.ui?.control === 'bullet-list' || field.type === 'string-list'
                        // The schema marks every content field full width; Figma pairs them in two columns.
                        const wide = list || contentFields.length === 1
                        let control = <V2TextField {...common} />
                        if (list) control = <BulletList {...common} />
                        else if (field.type === 'datetime') control = <DeadlineField {...common} />
                        else if (field.type === 'select') control = <V2SelectField {...common} options={field.options} />
                        return <div key={path} className={wide ? classes.full : classes.cell}>{control}</div>
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )
        })}
      </div>
      <div className={classes.addElement}>
        <button type="button" className={css.button} aria-expanded={picking} onClick={() => setPicking(!picking)}>
          {picking ? <IconX size={14} stroke={1.8} /> : <IconPlus size={14} stroke={1.8} />}
          {picking ? t('editorV2.form.common.close') : t('editorV2.form.extras.add', { title })}
        </button>
      </div>
      {picking && <div className={classes.pickBody}><ElementPicker cards={cards} onToggle={toggleElement} /></div>}
    </>
  )
}
